import { PayloadAction } from '@reduxjs/toolkit';
import { buffers, END, EventChannel, eventChannel } from 'redux-saga';
import {
    actionChannel,
    all,
    call,
    cancel,
    cancelled,
    delay,
    fork,
    put,
    race,
    SagaGenerator,
    select,
    take,
    takeEvery,
    takeLeading,
} from 'typed-redux-saga';

import { RootState } from '../../../../../init-store';
import { SELF } from '../../../../shared/constants';
import { getIsPMScreenRecordingEnabled } from '../../../../shared/features/config/selectors';
import { updateHasLastRecordAction } from '../../../../shared/features/download-record';
import { cleanupRecordingSessions } from '../../../../shared/features/download-record/sagas';
import { selectIsRvfcSkipEnabled } from '../../../../shared/features/flags/selectors';
import { muteRecordingCommand } from '../../../../shared/features/moderation/slice';
import { openConfirmNotificationAction } from '../../../../shared/features/notifications/confirm-notification';
import {
    hideNotification,
    NOTIFICATIONS_MAP,
    notify,
} from '../../../../shared/features/notifications/ps-notifications';
import { selectUserUid } from '../../../../shared/features/user/selectors';
import { getAudioContext } from '../../../../shared/helpers/audio-context';
import { setAudioTimeout, StopAudioTimerFunction } from '../../../../shared/helpers/audio-timer';
import { emitterEventChannelFactory } from '../../../../shared/helpers/emitterEventChannelFactory';
import { i18n } from '../../../../shared/helpers/i18n';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { cloudApi } from '../../../../shared/helpers/transport';
import { RECORD_MIME_TYPE } from '../../constants';
import { isRecordAllowed } from '../../helpers/is-record-allowed';
import { screenRecordingAction } from '../../iframe-sagas/slice';
import { IncomingPostMessage } from '../../iframe-sagas/types';
import { getConferenceConfig, getMyUserId, getParticipantById, getRoomId } from '../../selectors';
import {
    AvailableResolutions,
    CancelCallActionParams,
    NotificationId,
    RecordConferenceActionInterface,
    RecorderOptions,
    RecordSagaHelpers,
    StopRecordConference,
} from '../../types';
import { exportChatHistorySaga } from '../chat';
import { cancelCallAction, setParticipantById } from '../conference/slice';
import { drawConference } from './drawer';
import { clearOverlayManager } from './drawer/draw-conference-cached';
import { clearRvfcSkip, enableRvfcSkip } from './drawer/rvfc-skip/optimizer';
import { CompositeFileRecorder } from './file-recorders/composite-file-recorder';
import { IndexedDbRecorder } from './file-recorders/indexed-db-recorder';
import { PMRecorder } from './file-recorders/pm-recorder';
import { FileRecorder } from './file-recorders/types';
import { WSRecorder } from './file-recorders/ws-recorder';
import {
    recordConferenceAction,
    recordHaveBeenSavedAction,
    restartRecordConferenceAction,
    setIsLocalRecordingAction,
    setIsMeLocalRecordingAction,
    setIsRecording,
    setIsRecordingAction,
    stopRecordConferenceAction,
} from './slice';

const logger = createLogger('record-sagas');

const FPS = 30;
const ONE_FRAME_TIMEOUT = 1000 / FPS; // Время показа одного кадра
const MIN_FRAME_BY_FRAME_TIMEOUT = ONE_FRAME_TIMEOUT / 2; // Минимальное время простоя между отрисовкой кадров
const TIME_SLICE = 1000; // Величина чанков по времени, которые будут выдаваться из MediaRecorder

const RESOLUTIONS: Record<AvailableResolutions, RecordSize> = {
    720: { width: 1280, height: 720 },
    1080: { width: 1920, height: 1080 },
};

// Минимальное время показа нотифайки "Скачивание записи скоро начнется"
const MIN_SHOW_NOTIFICATION_WAITING_DOWNLOAD_TIMEOUT = 3000;

interface RecordSize {
    width: number;
    height: number;
}

interface RecordContext {
    canvas: HTMLCanvasElement;
    canvasContext: CanvasRenderingContext2D;
    mediaRecorder: MediaRecorder;
    fileRecorder: FileRecorder;

    activeAudioTracksMap: Map<MediaStreamTrack, AudioNode>;
    audioContext: AudioContext;
    audioDestinationNode: MediaStreamAudioDestinationNode;
    hideMyVideo: boolean;
    recordSize: RecordSize;
}

const refreshAudioTracks = (
    state: RootState,
    { activeAudioTracksMap, audioContext, audioDestinationNode }: RecordContext,
    helpers: RecordSagaHelpers
) => {
    const audioStreams = helpers.getAudioStreamTracks(state);
    const newAudioSet = new Set(audioStreams);
    const newTracks = [...newAudioSet].filter((track) => !activeAudioTracksMap.has(track));
    const removedTracks = [...activeAudioTracksMap.keys()].filter((track) => !newAudioSet.has(track));

    for (const newTrack of newTracks) {
        const sourceNode = audioContext.createMediaStreamSource(new MediaStream([newTrack]));
        sourceNode.connect(audioDestinationNode);
        activeAudioTracksMap.set(newTrack, sourceNode);
    }

    for (const removedTrack of removedTracks) {
        const node = activeAudioTracksMap.get(removedTrack);
        node?.disconnect();
        activeAudioTracksMap.delete(removedTrack);
    }
};

let wasDrawFrameError = false;

const drawFrame = (state: RootState, recordContext: RecordContext, helpers: RecordSagaHelpers) => {
    const { canvasContext, recordSize, hideMyVideo } = recordContext;

    try {
        const { width, height } = recordSize;
        drawConference(canvasContext, { width, height }, state, hideMyVideo, helpers);
    } catch (error) {
        // Залогируем ошибку в бустер, но не будем спамить каждый кадр. Только один раз
        if (!wasDrawFrameError) {
            wasDrawFrameError = true;
            logger.error(error, { method: 'drawFrame' });
        } else {
            console.error(error); // eslint-disable-line no-console
        }
    }
};

/**
 * @param {number} timeout
 */
function* audioDelaySaga(timeout: number) {
    let stopTimer: StopAudioTimerFunction | undefined;

    try {
        yield new Promise<number>((resolve) => {
            stopTimer = setAudioTimeout(resolve, timeout);
        });
    } finally {
        if (yield* cancelled()) {
            stopTimer?.();
        }
    }
}

const getMediaRecorderStreamTracksDiagnostics = (mediaRecorder: MediaRecorder) => {
    try {
        const tracks = mediaRecorder.stream.getTracks();
        return tracks.map((track) => ({
            kind: track.kind,
            id: track.id,
            label: track.label,
            readyState: track.readyState,
            muted: track.muted,
            enabled: track.enabled,
        }));
    } catch {
        return null;
    }
};

const createMediaRecorderDataEventChannel = (mediaRecorder: MediaRecorder): EventChannel<Blob> => {
    return eventChannel((emit) => {
        const dataListener = (blobEvent: BlobEvent) => emit(blobEvent.data);
        const stopListener = () => {
            const tracksDiagnostics = getMediaRecorderStreamTracksDiagnostics(mediaRecorder);
            const endedTracks = tracksDiagnostics?.filter((t) => t.readyState === 'ended') ?? [];
            const mutedTracks = tracksDiagnostics?.filter((t) => t.muted) ?? [];

            logger.event('Media recorder stopped', {
                additional: {
                    method: 'createMediaRecorderDataEventChannel',
                    recorderState: mediaRecorder.state,
                    tracks: tracksDiagnostics,
                    endedTracksCount: endedTracks.length,
                    mutedTracksCount: mutedTracks.length,
                    hasEndedTracks: endedTracks.length > 0,
                    hasMutedTracks: mutedTracks.length > 0,
                    endedTrackKinds: endedTracks.map((t) => t.kind),
                    mutedTrackKinds: mutedTracks.map((t) => t.kind),
                },
            });
            emit(END);
        };

        mediaRecorder.addEventListener('dataavailable', dataListener);
        mediaRecorder.addEventListener('stop', stopListener);

        return () => {
            mediaRecorder.removeEventListener('dataavailable', dataListener);
            mediaRecorder.removeEventListener('stop', stopListener);
        };
    }, buffers.expanding());
};

function* showErrorNotification(text: string) {
    yield* put(notify(NOTIFICATIONS_MAP[NotificationId.RECORDING_ERROR](text)));
}

/**
 * @param fileRecorder
 * @param mediaRecorderDataChannel
 */
function* saveMediaRecorderDataSaga(
    fileRecorder: FileRecorder,
    mediaRecorderDataChannel: EventChannel<Blob>
): SagaGenerator<void> {
    try {
        yield* call([fileRecorder, 'init']);
    } catch (error) {
        logger.error(error, { method: 'init' });
        throw error;
    }

    while (true) {
        const blob = yield* take(mediaRecorderDataChannel);

        try {
            yield* call([fileRecorder, 'addChunk'], blob);
        } catch (error) {
            logger.error(error, {
                method: 'addChunk',
                additional: {
                    stopReason: fileRecorder instanceof WSRecorder ? 'websocket_upload_error' : 'upload_error',
                    recorderType: fileRecorder instanceof WSRecorder ? 'ws' : 'file',
                },
            });

            if (fileRecorder instanceof IndexedDbRecorder) {
                yield* call(showErrorNotification, i18n('record', 'quota-exceed-error'));
            }

            break;
        }
    }
}

/**
 * @param {Object} recordContext
 * @param {Object} helpers - хелперы для отрисовки
 */
function* drawingFramesSaga(recordContext: RecordContext, helpers: RecordSagaHelpers): SagaGenerator<void> {
    while (true) {
        const state = yield* select();
        const startTime = performance.now();
        refreshAudioTracks(state, recordContext, helpers);
        drawFrame(state, recordContext, helpers);
        // Считаем время, которое осталось для других задач
        const busyTime = performance.now() - startTime;
        const restTime = ONE_FRAME_TIMEOUT - busyTime;

        // Рассчитываем, через сколько рисовать следующий кадр. Но ждем не меньше какого-то лимита,
        // чтобы не морозить страницу. Пусть лучше fps отрисовки чуть просядет
        const timeout = Math.max(MIN_FRAME_BY_FRAME_TIMEOUT, restTime);
        yield* call(audioDelaySaga, timeout);
    }
}

const onBeforeUnload = (event: BeforeUnloadEvent) => {
    // Cancel the event as stated by the standard.
    event.preventDefault();
    // Chrome requires returnValue to be set.
    event.returnValue = '';
};

/**
 *
 */
function* restartRecordingSaga({ payload }: PayloadAction<RecordConferenceActionInterface>) {
    yield* put(stopRecordConferenceAction());
    yield* put(recordConferenceAction(payload));
}

const getRecorderOptions = (state: RootState): MediaRecorderOptions => {
    const options: MediaRecorderOptions = {};

    const { recorderAudioBitsPerSecond: audioBitsPerSecond, recorderVideoBitsPerSecond: videoBitsPerSecond } =
        state.config;

    if (audioBitsPerSecond) {
        options.audioBitsPerSecond = audioBitsPerSecond;
    }

    if (videoBitsPerSecond) {
        options.videoBitsPerSecond = videoBitsPerSecond;
    }

    return options;
};

type IncomingAction =
    | PayloadAction<StopRecordConference | undefined>
    | PayloadAction<CancelCallActionParams | undefined>;

const isStopRecordAction = (action: IncomingAction): action is PayloadAction<StopRecordConference | undefined> =>
    action.type === stopRecordConferenceAction.type;

const getStopReason = (action: IncomingAction): StopRecordConference['reason'] | 'by_meeting_end' => {
    if (isStopRecordAction(action)) {
        return action.payload?.reason || 'by_user';
    }

    return 'by_meeting_end';
};

function* getAvailableStorageQuota() {
    if (navigator?.storage?.estimate) {
        return yield* call([navigator.storage, navigator.storage.estimate]);
    }
    return null;
}

function getLoggableCanvasParameters(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
    return {
        width: canvas.width,
        height: canvas.height,
        styleWidth: canvas.style.width,
        styleHeight: canvas.style.width,
        globalAlpha: ctx.globalAlpha,
        isInDOM: Boolean(canvas.parentNode),
    };
}

function subscribeVideoTrackEvents(track: MediaStreamTrack) {
    const additional = {
        method: 'subcribeVideoTrackEvents',
        feature: 'record',
        label: track.label,
        trackId: track.id,
    };

    track.addEventListener('ended', () => {
        logger.event('Canvas video track ended', { additional });
    });
    track.addEventListener('mute', () => {
        logger.event('Canvas video track mute', { additional });
    });
    track.addEventListener('unmute', () => {
        logger.event('Canvas video track unmute', { additional });
    });
}

function subcribeStreamEvents(stream: MediaStream) {
    stream.addEventListener('addtrack', (event: MediaStreamTrackEvent) => {
        logger.event('Added track to stream', {
            additional: {
                method: 'subcribeStreamEvents',
                trackId: event.track.id,
            },
        });
    });
}

function subscribeMediaRecorderEvents(track: MediaStreamTrack, mediaRecorder: MediaRecorder) {
    const additional = {
        feature: 'record',
        label: track.label,
        trackId: track.id,
    };

    mediaRecorder.addEventListener('error', (error) => {
        logger.error(error, {
            method: 'subscribeMediaRecorderEvents',
            additional,
        });
    });

    mediaRecorder.addEventListener('pause', () => {
        logger.event('Media recorder paused', {
            additional: {
                ...additional,
                method: 'subscribeMediaRecorderEvents',
            },
        });
    });
}

/**
 *
 */
function* recordSaga(
    {
        payload: { recorder, options } = { recorder: 'file', options: { resolution: 1080 } },
    }: PayloadAction<RecordConferenceActionInterface>,
    helpers: RecordSagaHelpers
) {
    if (!isRecordAllowed()) {
        logger.warn(`MIME-type ${RECORD_MIME_TYPE} is not supported`, {
            method: 'recordSaga',
        });
        return;
    }

    if (!helpers.canStartRecording()) {
        return;
    }

    const roomId = yield* select(getRoomId);

    countMetrika('meeting_screen', 'local_record', 'start');

    let saveRecord = true;
    let stopReason: StopRecordConference['reason'] | 'by_meeting_end' = 'by_user';
    let preserveForRecovery = false;

    try {
        yield* put(hideNotification(NotificationId.RECORDING_ERROR));

        // Удаляем лишние старые сессии, оставляя последнюю recoverable/валидную
        yield* call(cleanupRecordingSessions);

        const availableQuota = yield* call(getAvailableStorageQuota);
        logger.event('Meeting recording has been started', {
            additional: {
                method: 'recordSaga',
                storageQuota: availableQuota,
            },
        });

        if (recorder !== 'ws') {
            yield* put(setIsRecordingAction(true));
            window.addEventListener('beforeunload', onBeforeUnload);

            yield put(setIsLocalRecordingAction(true));
        }

        const state = yield* select();
        const stopRecordChannel = yield* actionChannel([cancelCallAction, stopRecordConferenceAction]);

        const canvas = document.createElement('canvas');
        canvas.style.position = 'fixed';
        canvas.style.top = '10px';
        canvas.style.left = '10px';
        canvas.style.visibility = 'hidden';
        canvas.style.transform = 'scale(0.3)';
        canvas.style.transformOrigin = 'top left';
        document.body.appendChild(canvas);

        const canvasContext = canvas.getContext('2d');

        const audioContext = getAudioContext();
        if (!canvasContext || !options.resolution || !audioContext) {
            return;
        }
        logger.event('Canvas params before recording', {
            additional: {
                method: 'recordSaga',
                ...getLoggableCanvasParameters(canvas, canvasContext),
            },
        });

        const recordSize = RESOLUTIONS[options.resolution];
        canvas.style.display = '';
        canvas.width = recordSize.width;
        canvas.height = recordSize.height;

        const audioDestinationNode = audioContext.createMediaStreamDestination();
        const activeAudioTracksMap = new Map<MediaStreamTrack, AudioNode>();

        const stream = canvas.captureStream(FPS);
        subcribeStreamEvents(stream);
        const tracks = stream.getVideoTracks();
        if (tracks.length > 1) {
            logger.event('Several video tracks in stream', {
                additional: {
                    method: 'recordSaga',
                    count: tracks.length,
                },
            });
        }
        const videoTrack = tracks[0];

        subscribeVideoTrackEvents(videoTrack);
        const mixedStream = new MediaStream([audioDestinationNode.stream.getAudioTracks()[0], videoTrack]);

        const startRecordTime = Date.now();
        const mediaRecorder = new MediaRecorder(mixedStream, { mimeType: RECORD_MIME_TYPE });
        subscribeMediaRecorderEvents(videoTrack, mediaRecorder);

        let fileRecorder: FileRecorder;
        const { ws } = options as RecorderOptions;
        if (recorder === 'ws' && ws) {
            fileRecorder = new WSRecorder({ url: ws });
            const restartChannel = emitterEventChannelFactory(fileRecorder as WSRecorder, 'restart');
            yield* fork(function* () {
                yield* take(restartChannel);
                yield* put(restartRecordConferenceAction({ recorder, options }));
            });
        } else {
            const isPMScreenRecordingEnabled = yield* select(getIsPMScreenRecordingEnabled);
            const uid = (yield* select(selectUserUid)) || '';

            if (isPMScreenRecordingEnabled) {
                const pmRecorder = new PMRecorder({ uid, mimeType: RECORD_MIME_TYPE, chunkDuration: TIME_SLICE });
                const indexedDbBackupRecorder = new IndexedDbRecorder({
                    uid,
                    mimeType: RECORD_MIME_TYPE,
                    chunkDuration: TIME_SLICE,
                    recorderKind: 'pm-backup',
                });
                fileRecorder = new CompositeFileRecorder({
                    primary: pmRecorder,
                    backup: indexedDbBackupRecorder,
                });
                yield* fork(handlePostMessageSaga, pmRecorder);
            } else {
                fileRecorder = new IndexedDbRecorder({
                    uid,
                    mimeType: RECORD_MIME_TYPE,
                    chunkDuration: TIME_SLICE,
                    recorderKind: 'indexeddb',
                });
            }
        }

        const recordContext: RecordContext = {
            canvas,
            canvasContext,

            mediaRecorder,
            fileRecorder,

            audioContext,
            audioDestinationNode,
            activeAudioTracksMap,
            hideMyVideo: recorder === 'ws',
            recordSize,
        };

        refreshAudioTracks(state, recordContext, helpers);

        const dataAvailableChannel = createMediaRecorderDataEventChannel(mediaRecorder);
        const pageHideChannel = eventChannel<PayloadAction<StopRecordConference>>((emit) => {
            const handler = () => {
                emit({ type: stopRecordConferenceAction.type, payload: { reason: 'tab_close' } });
            };

            window.addEventListener('pagehide', handler, { once: true });

            return () => {
                window.removeEventListener('pagehide', handler);
            };
        });

        try {
            const stopRecordTask = yield* fork(function* () {
                const { stopAction, pageHideAction } = (yield* race({
                    stopAction: take<IncomingAction>(stopRecordChannel),
                    pageHideAction: take<PayloadAction<StopRecordConference>>(pageHideChannel),
                })) as {
                    stopAction?: IncomingAction;
                    pageHideAction?: PayloadAction<StopRecordConference>;
                };

                const action = stopAction || pageHideAction;
                if (!action) {
                    return;
                }

                stopReason = getStopReason(action);
                preserveForRecovery = stopReason === 'tab_close';
                countMetrika('meeting_screen', 'local_record', 'stop', stopReason);

                if (isStopRecordAction(action) && action.payload?.reason === 'tab_close') {
                    try {
                        if (mediaRecorder.state === 'recording') {
                            mediaRecorder.requestData();
                        }
                    } catch (error) {
                        logger.error(error, {
                            method: 'requestDataOnPageHide',
                        });
                    }
                }

                if (mediaRecorder.state !== 'inactive') {
                    mediaRecorder.stop();
                }

                const availableQuota = yield* call(getAvailableStorageQuota);
                logger.event('Meeting recording has been stopped', {
                    additional: {
                        method: 'recordSaga',
                        storageQuota: availableQuota,
                        reason: stopReason,
                    },
                });

                if (isStopRecordAction(action) && action.payload?.remove) {
                    saveRecord = false;
                }
            });

            if (yield* select(selectIsRvfcSkipEnabled)) {
                enableRvfcSkip();
            }

            const drawingFramesTask = yield* fork(drawingFramesSaga, recordContext, helpers);
            const recorderOptions = yield* select(getRecorderOptions);

            mediaRecorder.start(TIME_SLICE, recorderOptions);

            // Ждем, пока все сохранится. Данная задача автоматически остановится, когда будет вызван
            // mediaRecorder.stop, а все чанки сохранятся в базу данных
            yield* call(saveMediaRecorderDataSaga, fileRecorder, dataAvailableChannel);

            // Завершаем остальные задачи
            yield* cancel([stopRecordTask, drawingFramesTask]);
        } catch (error) {
            logger.error(error, {
                method: 'recordSaga',
            });
        } finally {
            if (mediaRecorder.state !== 'inactive') {
                yield* call([mediaRecorder, mediaRecorder.stop]);
            }

            // Меняем сразу статус записи (чтобы участник мог начать вторую, не дожидаясь сохранения текущей)
            yield* put(setIsRecordingAction(false));
            yield* put(setIsLocalRecordingAction(false));

            [...activeAudioTracksMap.values()].forEach((node) => node.disconnect());
            activeAudioTracksMap.clear();
            dataAvailableChannel.close();
            stopRecordChannel.close();
            pageHideChannel.close();

            logger.event('Canvas params after recording', {
                additional: {
                    method: 'recordSaga',
                    ...getLoggableCanvasParameters(canvas, canvasContext),
                },
            });
            canvas.remove();
            clearOverlayManager();
            clearRvfcSkip();
        }

        if (saveRecord && recorder === 'file') {
            const duration = Date.now() - startRecordTime;
            const durationInMinutes = Math.round(duration / 1000 / 60);
            countMetrika('meeting_screen', 'duration', durationInMinutes);
        }

        const shouldPreserveForRecovery = saveRecord && preserveForRecovery;
        const closePayload = { withoutSave: !saveRecord, preserveForRecovery: shouldPreserveForRecovery };

        if (saveRecord && fileRecorder instanceof IndexedDbRecorder && !shouldPreserveForRecovery) {
            // Запоминаем время начала показа нотифайки "Подождите, запись скоро начнет скачиваться"
            let startTimeOfShowDownloadNotification: number | undefined;

            const downloadNotificationTask = yield* fork(function* () {
                yield* delay(2000);

                yield* put(notify(NOTIFICATIONS_MAP[NotificationId.RECORDING_WAITING_DOWNLOAD]()));
                startTimeOfShowDownloadNotification = Date.now();
            });

            const closeResult = yield* call([fileRecorder, fileRecorder.close], closePayload);
            if (closeResult.status !== 'discarded') {
                yield* put(updateHasLastRecordAction());
            }

            // Мы должны показать нотифайку "Подождите, запись скоро начнет скачиваться" минимум 3 секунды. Поэтому если
            // нотифайку начали показывать, а 3 секунды еще не прошло, подождем оставшееся время и уберем нотифайку.
            yield* cancel(downloadNotificationTask);
            if (startTimeOfShowDownloadNotification) {
                const showTime = Date.now() - startTimeOfShowDownloadNotification;
                if (showTime < MIN_SHOW_NOTIFICATION_WAITING_DOWNLOAD_TIMEOUT) {
                    yield* delay(MIN_SHOW_NOTIFICATION_WAITING_DOWNLOAD_TIMEOUT - showTime);
                }

                yield* put(hideNotification(NotificationId.RECORDING_WAITING_DOWNLOAD));
            }
        } else {
            const closeResult = yield* call([fileRecorder, fileRecorder.close], closePayload);
            if (saveRecord && closeResult.status !== 'discarded') {
                yield* put(updateHasLastRecordAction());
            }
        }
    } catch (error) {
        const availableQuota = yield* call(getAvailableStorageQuota);
        logger.error(error, {
            method: 'startRecordSaga',
            additional: {
                storageQuota: availableQuota,
            },
        });
    } finally {
        window.removeEventListener('beforeunload', onBeforeUnload);

        if (saveRecord) {
            yield* put(recordHaveBeenSavedAction());
            yield* call(exportChatHistorySaga, roomId);
        }
    }
}

function* handlePostMessageSaga(pmRecorder: PMRecorder) {
    yield* takeEvery(screenRecordingAction, ({ payload }: PayloadAction<IncomingPostMessage>) =>
        pmRecorder.handlePostMessage(payload)
    );
}

function* setIsMeLocalRecordingSaga(action: PayloadAction<boolean>) {
    const isLocalRecording = action.payload;
    const me = yield* select(getParticipantById, SELF);
    if (me) {
        yield* put(setParticipantById({ ...me, isLocalRecording }));
    }
}

function* setIsRecordingSaga(action: PayloadAction<boolean>) {
    yield* put(setIsRecording(action.payload));
    yield* put(setIsMeLocalRecordingAction(action.payload));
}

function* muteRecordingCommandHandler({ payload: withoutRemoval }: ReturnType<typeof muteRecordingCommand>) {
    yield* put(stopRecordConferenceAction({ reason: 'moderation_command', remove: !withoutRemoval }));
    yield* put(
        openConfirmNotificationAction({
            headerText: i18n('moderation', 'notification-confirm-mute-recording-header'),
            contentText: i18n(
                'moderation',
                withoutRemoval
                    ? 'notification-confirm-mute-recording-without-removal-content'
                    : 'notification-confirm-mute-recording-content'
            ),
        })
    );
}

/**
 * Рутовая сага для записи
 */
export function* rootRecordSaga(helpers: RecordSagaHelpers) {
    yield all([
        takeLeading(recordConferenceAction.type, function* (action: PayloadAction<RecordConferenceActionInterface>) {
            yield* call(recordSaga, action, helpers);
        }),
        takeEvery(restartRecordConferenceAction.type, restartRecordingSaga),
        takeEvery(setIsRecordingAction.type, setIsRecordingSaga),
        takeEvery(setIsMeLocalRecordingAction.type, setIsMeLocalRecordingSaga),
        takeEvery(muteRecordingCommand, muteRecordingCommandHandler),
    ]);
}

export function* changeIsLocalRecordingSaga({ payload }: PayloadAction<boolean>) {
    const conferenceConfig = yield* select(getConferenceConfig);
    const myUserId = yield* select(getMyUserId);

    if (!conferenceConfig || !myUserId) {
        return;
    }

    try {
        yield* call([cloudApi, cloudApi.setPeersState], conferenceConfig.resolved_uri, myUserId, {
            is_local_recording: payload,
        });
    } catch (error) {
        logger.error(error, {
            method: 'updateLocalRecordingStateSaga',
        });
    }
}
